import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Query, Mutation, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';

import Link from '../components/Link';
import { Loader } from '../components/Loader';
import { Error } from '../components/Error';
import GenerateMetaTags from '../components/GenerateMetaTags';
import Breadcrumbs from '../components/Breadcrumbs';
import Article, { Title } from '../components/Article';
import ImageSlider from '../components/ImageSlider';
import CarPartInfo from '../components/CarPartInfo';
import {
  CartButton,
  RequestButton,
  Wrapper as CartWrapper,
  Row as CartRow
} from '../components/AddToCart';
import GuidanceNotice from '../components/GuidanceNotice';
import { withAppStorage } from '../utils/appStorage';
import { GET_CART } from './CartPage';
import { TransitionWrapper } from '../components/TransitionWrapper';

import { dataLayer } from '../utils/window';

const GET_PART = gql`
  query getPart($carPartItemId: Int!) {
    newPartCodes: getNewPartCodes
    carPart: getPart(Indx: $carPartItemId) {
      id: Indx
      name: displayName
      stockNumber: Lagernummer
      quality: Kvalitet
      country: Land
      originalCode: Originalkod
      boschNumber: Boschnummer
      articleNumber: ArtnrSok
      carLabel: Bilanmarkning
      regNumber: Registreringsnr
      vin: Chassinummer
      chassis: Kaross
      engine: Motor
      gearBox: Vaxellada
      carColor: Bilfarg
      modelYear: Arsmodell
      mileage: KM
      images: Bilder
      price: Pris
      shipping: Frakt
      partLabel1: Anmarkning1
      partLabel2: Anmarkning2
      qualityDescription: KvalitetsAnm
    }
  }
`;

const ADD_PRODUCT_TO_CART = gql`
  mutation addProductToCart(
    $cartId: String
    $productIndx: String!
    $alfa: String
    $articleNumber: String
  ) {
    cart: addProductToCart(
      cartId: $cartId
      productIndx: $productIndx
      alfa: $alfa
      articleNumber: $articleNumber
    ) {
      id
      sumProducts
      sumShipping
      shippingDiscount
      products {
        price
        name
        productIndx
        part {
          id: Indx
          name: displayName
          stockNumber: Lagernummer
          images: Bilder
        }
      }
    }
  }
`;

const NewPartBuySection = withApollo(
  class NewPartBuySectionClass extends Component {
    constructor(props) {
      super(props);

      this.state = {
        loading: true,
        foundPart: null
      };
    }

    componentDidMount() {
      let { partCodes, part, client } = this.props;

      if (!part.articleNumber) {
        this.setState({
          loading: false
        });

        return;
      }

      Promise.all(
        partCodes.map(code => {
          return new Promise((resolve, reject) => {
            client
              .query({
                query: gql`
                  query getNewPart($part: String, $alfa: String) {
                    part: getNewPart(part: $part, alfa: $alfa) {
                      fnr
                      alfa
                      part
                      beh
                      arttxt
                      pris
                      pristillegg
                    }
                  }
                `,
                variables: {
                  part:
                    part.articleNumber && part.articleNumber.replace(/\s/g, ''),
                  alfa: code
                }
              })
              .catch(error => {
                return {
                  data: {
                    part: null
                  }
                };
              })
              .then(resolve);
          });
        })
      ).then(data => {
        let foundPart = null;

        for (let key in data) {
          if (data[key].data.part) {
            foundPart = data[key].data.part;
            break;
          }
        }

        this.setState({
          loading: false,
          foundPart: foundPart
        });
      });
    }

    render() {
      let { loading, foundPart } = this.state;
      let { part, onClick, fullWidth, ...props } = this.props;

      return (
        <div>
          {loading && <Loader />}
          {!loading &&
            foundPart && (
              <CartButton
                {...props}
                onClick={() => {
                  onClick &&
                    onClick({
                      name: `Nydel: ${foundPart.alfa} ${part.articleNumber} - ${
                        part.name
                      }`,
                      articleNumber: foundPart.part,
                      ...foundPart
                    });
                }}
                newItem={true}
                fullWidth={fullWidth}
                part={{
                  ...part,
                  price: foundPart.pris,
                  name: `Nydel: ${foundPart.alfa} ${part.articleNumber} - ${
                    part.name
                  }`
                }}
              />
            )}
        </div>
      );
    }
  }
);

const addToCart = ({
  addProductToCart,
  cartId,
  appStorage,
  part,
  alfa,
  articleNumber
}) => {
  addProductToCart({
    variables: {
      cartId: cartId || undefined,
      productIndx: part.id.toString(),
      alfa,
      articleNumber
    },
    update: (store, { data: { cart } }) => {
      let data = null;

      appStorage.set('cartId', cart.id);

      try {
        data = store.readQuery({
          query: GET_CART,
          variables: { id: cart.id }
        });
      } catch (e) {}
      if (!data) {
        store.writeQuery({
          query: GET_CART,
          data: {
            cart
          },
          variables: { id: cart.id }
        });

        return;
      }

      data.cart = cart;
      store.writeQuery({
        query: GET_CART,
        data,
        variables: { id: cart.id }
      });
    }
  }).then(({ data, error }) => {
    if (error) {
      // TODO: Handle error!
      console.log(error);
    }

    dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'nok',
        add: {
          products: [
            {
              ...part,
              quantity: 1
            }
          ]
        }
      }
    });

    toast(
      <p>
        {part.name + ' lagt til i handlekurv!'}
        <br />
        <Link to="/handlekurv/">Se i handlekurven</Link>
      </p>,
      {
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      }
    );

    if (!cartId && data.cart) {
      appStorage.set('cartId', data.cart.id);
    }
  });
};

export default withAppStorage(function CarPartItemPage({
  match,
  appStorageData,
  appStorage
}) {
  const carPartItemId = match.params.carPartItemId
    ? Number(match.params.carPartItemId)
    : null;

  return (
    <TransitionWrapper>
      <Query query={GET_PART} variables={{ carPartItemId: carPartItemId }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Loader />;
          }

          if (error) {
            return <Error error={error} />;
          }

          if (!data.carPart) {
            return <div>Ingenting funnet</div>;
          }

          const {
            id,
            name,
            stockNumber,
            images,
            price,
            shipping,
            articleNumber
          } = data.carPart;
          const newPartCodes = data.newPartCodes;
          const part = data.carPart;

          dataLayer.push({
            event: 'productView',
            ecommerce: {
              detail: {
                actionField: { list: 'Bildel' },
                products: [part]
              }
            }
          });

          return (
            <Article>
              <Helmet>
                {GenerateMetaTags({
                  title: name
                  //description: name
                })}
              </Helmet>

              <Breadcrumbs breadcrumbs={null} />

              <Title>{name}</Title>

              <ImageSlider images={images} />

              <CarPartInfo part={part} />

              <CartWrapper>
                {!!part.shipping && (
                  <Mutation mutation={ADD_PRODUCT_TO_CART}>
                    {(addProductToCart, { error, loading }) => (
                      <div>
                        {appStorageData.cartId && (
                          <Query
                            query={GET_CART}
                            variables={{
                              id: appStorageData.cartId
                            }}
                          >
                            {({ data, error, loading }) => (
                              <CartRow>
                                {!!part.price && (
                                  <CartButton
                                    isInCart={
                                      data &&
                                      data.cart &&
                                      data.cart.products &&
                                      data.cart.products.some(
                                        p => p.part.id === part.id
                                      )
                                    }
                                    onClick={() => {
                                      addToCart({
                                        part,
                                        appStorage,
                                        addProductToCart,
                                        cartId: appStorageData.cartId
                                      });
                                    }}
                                    newItem={false}
                                    part={part}
                                    fullWidth
                                  />
                                )}
                                {!part.price && (
                                  <RequestButton part={part} fullWidth />
                                )}
                                <NewPartBuySection
                                  partCodes={newPartCodes}
                                  part={part}
                                  isInCart={
                                    data &&
                                    data.cart &&
                                    data.cart.products &&
                                    data.cart.products.some(
                                      p => p.part.id === part.id
                                    )
                                  }
                                  fullWidth
                                  onClick={newPart => {
                                    return;
                                    addToCart({
                                      part,
                                      appStorage,
                                      addProductToCart,
                                      alfa: newPart.alfa,
                                      articleNumber: newPart.articleNumber,
                                      cartId: appStorageData.cartId
                                    });
                                  }}
                                />
                              </CartRow>
                            )}
                          </Query>
                        )}

                        {!appStorageData.cartId && (
                          <CartRow>
                            {!!part.price && (
                              <CartButton
                                isInCart={false}
                                onClick={() => {
                                  addToCart({
                                    part,
                                    appStorage,
                                    addProductToCart,
                                    cartId: appStorageData.cartId
                                  });
                                }}
                                newItem={false}
                                fullWidth
                                part={part}
                              />
                            )}
                            {!part.price && (
                              <RequestButton part={part} fullWidth />
                            )}
                            <NewPartBuySection
                              partCodes={newPartCodes}
                              part={part}
                              isInCart={false}
                              fullWidth
                              onClick={newPart => {
                                return;
                                addToCart({
                                  part,
                                  appStorage,
                                  addProductToCart,
                                  alfa: newPart.alfa,
                                  articleNumber: newPart.articleNumber,
                                  cartId: appStorageData.cartId
                                });
                              }}
                            />
                          </CartRow>
                        )}
                      </div>
                    )}
                  </Mutation>
                )}

                {!part.shipping && (
                  <CartRow>
                    <RequestButton part={part} fullWidth />
                  </CartRow>
                )}
              </CartWrapper>
            </Article>
          );
        }}
      </Query>

      <GuidanceNotice />
    </TransitionWrapper>
  );
});
